<script setup lang="ts">
import { computed } from 'vue';
import { useLayout } from '@/composables/useLayout';

type Item = {
  id: string | number;
  slug: string;
  image: string;
};

type Props = {
  title?: string;
  items: Item[];
  desktopSizeFactor?: number;
  zIndex?: number;
};

const {
  title = 'Itens Relacionados',
  items,
  desktopSizeFactor = 1,
  zIndex = 50,
} = defineProps<Props>();

const emit = defineEmits(['update:modelValue']);

const isVisible = defineModel<boolean>('modelValue', {
  required: false,
  default: false,
});

function closeDialog() {
  isVisible.value = false;
  emit('update:modelValue', false);
}

const { isMobile } = useLayout();
const { header, bottomMenu } = storeToRefs(useLayoutStore());
const headerHeight = computed(() => header.value.height);
const bottomMenuHeight = computed(() => bottomMenu.value.height || 0);

// Store original overflow to restore it properly
let originalOverflow = '';

// Prevent body scroll when modal is open
watch(isVisible, (newVal) => {
  if (import.meta.client) {
    if (newVal) {
      originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      document.body.classList.add('modal-open');
    } else {
      document.body.style.overflow = originalOverflow;
      document.body.classList.remove('modal-open');
    }
  }
});

// Cleanup on unmount to prevent memory leaks
onUnmounted(() => {
  if (import.meta.client && isVisible.value) {
    document.body.style.overflow = originalOverflow;
    document.body.classList.remove('modal-open');
  }
});

const BASE_MODAL_WIDTH = 352;
const BASE_IMAGE_WIDTH_MOBILE = 100;
const BASE_IMAGE_HEIGHT_MOBILE = 115;
const BASE_IMAGE_WIDTH_DESKTOP = 72;
const BASE_IMAGE_HEIGHT_DESKTOP = 83;
const BASE_GAP = 8;

const scaledModalWidth = computed(() => {
  return isMobile.value
    ? BASE_MODAL_WIDTH
    : BASE_MODAL_WIDTH * desktopSizeFactor;
});

const scaledImageWidth = computed(() => {
  return isMobile.value
    ? BASE_IMAGE_WIDTH_MOBILE
    : BASE_IMAGE_WIDTH_DESKTOP * desktopSizeFactor;
});

const scaledImageHeight = computed(() => {
  return isMobile.value
    ? BASE_IMAGE_HEIGHT_MOBILE
    : BASE_IMAGE_HEIGHT_DESKTOP * desktopSizeFactor;
});

const scaledGap = computed(() => {
  return isMobile.value ? BASE_GAP : BASE_GAP * desktopSizeFactor;
});

const modalWrapperStyle = computed(() => ({
  width: `${scaledModalWidth.value}px`,
  maxWidth: `${scaledModalWidth.value}px`,
}));

const gridStyle = computed(() => ({
  gap: `${scaledGap.value}px`,
}));

const imageStyle = computed(() => ({
  width: `${scaledImageWidth.value}px`,
  height: `${scaledImageHeight.value}px`,
}));
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isVisible"
      :class="$style.modal"
      :style="{
        top: headerHeight + 'px',
        '--header-height': headerHeight + 'px',
        '--bottom-menu-height': bottomMenuHeight + 'px',
        zIndex: zIndex,
      }"
      @click.self.stop="closeDialog"
    >
      <div
        :class="$style.wrapper"
        :style="modalWrapperStyle"
      >
        <div :class="$style.header">
          <NuxtIcon
            class="text-[36px] text-white"
            draggable="false"
            :filled="true"
            name="home/jogos-ao-vivo"
          />
          <p class="text-lg font-bold text-white">{{ title }}</p>
          <button
            :class="$style.closeButton"
            @click="closeDialog"
          >
            <NuxtIcon
              class="font-bold text-[16px]"
              name="close"
            />
          </button>
        </div>
        <div :class="$style.inner">
          <div
            :class="[isMobile ? 'grid grid-cols-3' : 'grid grid-cols-4']"
            :style="gridStyle"
          >
            <div
              v-for="item in items"
              :key="item.id"
              class="flex flex-col items-center cursor-pointer hover:brightness-75"
              @click="
                navigateTo(
                  item.slug.startsWith('sports/')
                    ? `/${item.slug}`
                    : `/games/${item.slug}`,
                )
              "
            >
              <NuxtImg
                class="object-fill rounded-[4px]"
                draggable="false"
                :src="item.image"
                :style="imageStyle"
              />
            </div>
          </div>
        </div>
        <div :class="$style.footer">
          <div
            :class="$style.footerCloseButton"
            tag="button"
            @click="closeDialog"
          >
            <p
              class="text-white font-extrabold text-[13px] self-center text-center mx-auto"
              >Fechar</p
            >
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style module lang="postcss">
.modal {
  @apply fixed inset-0 bg-[#030C27c5] z-[50] flex justify-center items-center lg:items-center select-none backdrop-blur-sm lg:pb-0;
  /* Usa o maior valor entre bottomMenu.height e safe-area-inset-bottom */
  padding-bottom: max(
    var(--bottom-menu-height, 0px),
    env(safe-area-inset-bottom, 0px)
  );

  .wrapper {
    @apply relative flex pb-2 flex-col mx-auto transform text-left align-middle shadow-xl bg-bg-primary rounded-[10px] mt-[20px] lg:mt-0;
    border: 1.038px solid #1d2329;
    /* Usa 100dvh para considerar viewport dinâmico em mobile */
    max-height: calc(100dvh - var(--header-height) - 100px);
  }

  .header {
    @apply flex items-center justify-between px-5 pt-5 pb-4 border-b border-white/70;
  }

  .closeButton {
    @apply w-[32px] h-[32px] text-white/70 hover:text-white transition-colors flex justify-center items-center;

    svg {
      @apply w-[1.5em] h-[1.5em];
    }
  }

  .inner {
    @apply overflow-y-auto px-5;
  }

  .footer {
    @apply px-5 pt-4 pb-5 border-t border-white/70;
  }

  .footerCloseButton {
    @apply relative w-full h-8 rounded-md border border-solid border-white/50 bg-white/10 opacity-100 px-[10px] flex gap-[7px] cursor-pointer hover:brightness-75;
  }
}
</style>
